import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LoadingSpinner } from "@gradebook/ui";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Layout } from "@/components/layout/Layout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { SchoolSlugGuard } from "@/components/auth/SchoolSlugGuard";
import { useAuthStore } from "@/store/authStore";

// Route components are code-split via React.lazy so each page ships in its own
// chunk and the initial entry bundle stays small. These pages use named exports,
// so map them to the { default } shape React.lazy expects.
const Login = lazy(() =>
  import("@/pages/auth/Login").then((m) => ({ default: m.Login }))
);
const Signup = lazy(() =>
  import("@/pages/auth/Signup").then((m) => ({ default: m.Signup }))
);
const Dashboard = lazy(() =>
  import("@/pages/dashboard/Dashboard").then((m) => ({ default: m.Dashboard }))
);
const Gradebook = lazy(() =>
  import("@/pages/gradebook/Gradebook").then((m) => ({ default: m.Gradebook }))
);
const Students = lazy(() =>
  import("@/pages/students/Students").then((m) => ({ default: m.Students }))
);
const StudentDetail = lazy(() =>
  import("@/pages/students/StudentDetail").then((m) => ({
    default: m.StudentDetail,
  }))
);
const Courses = lazy(() =>
  import("@/pages/courses/Courses").then((m) => ({ default: m.Courses }))
);
const CourseDetail = lazy(() =>
  import("@/pages/courses/CourseDetail").then((m) => ({
    default: m.CourseDetail,
  }))
);
const Settings = lazy(() =>
  import("@/pages/settings/Settings").then((m) => ({ default: m.Settings }))
);
const BulkImport = lazy(() =>
  import("@/pages/import/BulkImport").then((m) => ({ default: m.BulkImport }))
);
const Calendar = lazy(() =>
  import("@/pages/calendar/Calendar").then((m) => ({ default: m.Calendar }))
);
const MyGrades = lazy(() =>
  import("@/pages/grades/MyGrades").then((m) => ({ default: m.MyGrades }))
);
const YearEndGrades = lazy(() =>
  import("@/pages/grades/YearEndGrades").then((m) => ({
    default: m.YearEndGrades,
  }))
);
const ParentDashboard = lazy(() =>
  import("@/pages/parent/ParentDashboard").then((m) => ({
    default: m.ParentDashboard,
  }))
);
const ChildGrades = lazy(() =>
  import("@/pages/parent/ChildGrades").then((m) => ({ default: m.ChildGrades }))
);
const ApprovalQueue = lazy(() =>
  import("@/pages/admin/ApprovalQueue").then((m) => ({
    default: m.ApprovalQueue,
  }))
);
const GradeChangeRequests = lazy(() =>
  import("@/pages/admin/GradeChangeRequests").then((m) => ({
    default: m.GradeChangeRequests,
  }))
);
const Parents = lazy(() =>
  import("@/pages/admin/Parents").then((m) => ({ default: m.Parents }))
);
const Teachers = lazy(() =>
  import("@/pages/admin/Teachers").then((m) => ({ default: m.Teachers }))
);
const Departments = lazy(() =>
  import("@/pages/admin/Departments").then((m) => ({ default: m.Departments }))
);
const AcademicYears = lazy(() =>
  import("@/pages/admin/AcademicYears").then((m) => ({
    default: m.AcademicYears,
  }))
);
const AcademicYearSetup = lazy(() =>
  import("@/pages/admin/AcademicYearSetup").then((m) => ({
    default: m.AcademicYearSetup,
  }))
);
const AuditLog = lazy(() =>
  import("@/pages/admin/AuditLog").then((m) => ({ default: m.AuditLog }))
);
const GradePosting = lazy(() =>
  import("@/pages/admin/GradePosting").then((m) => ({
    default: m.GradePosting,
  }))
);
const MySubmissions = lazy(() =>
  import("@/pages/teacher/MySubmissions").then((m) => ({
    default: m.MySubmissions,
  }))
);
const MyGradeChangeRequests = lazy(() =>
  import("@/pages/teacher/MyGradeChangeRequests").then((m) => ({
    default: m.MyGradeChangeRequests,
  }))
);

function RootRedirect() {
  const user = useAuthStore((state) => state.user);

  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  return <Navigate to={`/${user.schoolSlug}/dashboard`} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner text="Loading…" />}>
          <Routes>
            {/* Public routes */}
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/signup" element={<Signup />} />

            {/* Protected routes with school slug */}
            <Route
              path="/:schoolSlug"
              element={
                <ProtectedRoute>
                  <SchoolSlugGuard>
                    <Layout />
                  </SchoolSlugGuard>
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route
                path="gradebook"
                element={
                  <ProtectedRoute roles={["teacher", "admin"]}>
                    <Gradebook />
                  </ProtectedRoute>
                }
              />
              <Route
                path="students"
                element={
                  <ProtectedRoute roles={["admin", "teacher"]}>
                    <Students />
                  </ProtectedRoute>
                }
              />
              <Route
                path="students/:id"
                element={
                  <ProtectedRoute roles={["admin", "teacher"]}>
                    <StudentDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="courses"
                element={
                  <ProtectedRoute roles={["admin", "teacher"]}>
                    <Courses />
                  </ProtectedRoute>
                }
              />
              <Route
                path="courses/:id"
                element={
                  <ProtectedRoute roles={["admin", "teacher"]}>
                    <CourseDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="calendar"
                element={
                  <ProtectedRoute roles={["teacher", "admin", "student"]}>
                    <Calendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="my-grades"
                element={
                  <ProtectedRoute roles={["student"]}>
                    <MyGrades />
                  </ProtectedRoute>
                }
              />
              {/* The year-end subject rollup (AR-12), one page under three paths.
                  The guards here only keep a role off a path that makes no sense
                  for it; who may read whose record is decided by the API. */}
              <Route
                path="year-end-grades"
                element={
                  <ProtectedRoute roles={["student", "parent"]}>
                    <YearEndGrades />
                  </ProtectedRoute>
                }
              />
              <Route
                path="students/:id/year-end-grades"
                element={
                  <ProtectedRoute roles={["admin", "teacher"]}>
                    <YearEndGrades />
                  </ProtectedRoute>
                }
              />
              <Route
                path="parent/children/:studentId/year-end-grades"
                element={
                  <ProtectedRoute roles={["parent"]}>
                    <YearEndGrades />
                  </ProtectedRoute>
                }
              />
              <Route
                path="parent"
                element={
                  <ProtectedRoute roles={["parent"]}>
                    <ParentDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="parent/children/:studentId/grades"
                element={
                  <ProtectedRoute roles={["parent"]}>
                    <ChildGrades />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="import"
                element={
                  <ProtectedRoute roles={["teacher", "admin"]}>
                    <BulkImport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/approvals"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <ApprovalQueue />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/grade-changes"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <GradeChangeRequests />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/parents"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Parents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/teachers"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Teachers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/departments"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <Departments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/academic-years"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <AcademicYears />
                  </ProtectedRoute>
                }
              />
              {/* "new" is matched before ":yearId" so it is never read as an id. */}
              <Route
                path="admin/academic-years/new"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <AcademicYearSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/academic-years/:yearId"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <AcademicYearSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/audit-log"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <AuditLog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/grade-posting"
                element={
                  <ProtectedRoute roles={["admin"]}>
                    <GradePosting />
                  </ProtectedRoute>
                }
              />
              <Route
                path="my-submissions"
                element={
                  <ProtectedRoute roles={["teacher"]}>
                    <MySubmissions />
                  </ProtectedRoute>
                }
              />
              {/* Admins can raise a change request too (the API allows it), so this
                  is not teacher-only. */}
              <Route
                path="my-grade-changes"
                element={
                  <ProtectedRoute roles={["teacher", "admin"]}>
                    <MyGradeChangeRequests />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Redirect root to school-specific dashboard */}
            <Route path="/" element={<RootRedirect />} />

            {/* Catch all - redirect to root which handles auth */}
            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
